import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Globe, Settings, Upload, Palette, Eye, Save, 
  Image, Type, CheckCircle, RefreshCw, Mail, Send, Bell,
  Plus, Pencil, Trash2, GripVertical, Clock, List, AlertTriangle,
  User, Zap, History, TestTube, ThumbsUp, ExternalLink
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import Sidebar from "../components/Sidebar";

const API = (process.env.REACT_APP_BACKEND_URL || "");

const EncuestasPortalPage = () => {
  const [activeTab, setActiveTab] = useState("portal");
  const [config, setConfig] = useState({
    logo_url: "",
    imagen_bienvenida_url: "",
    imagen_fondo_url: "",
    usar_imagen_bienvenida: false,
    usar_imagen_fondo: false,
    color_primario: "#0C314C",
    color_secundario: "#45E69A",
    color_fondo: "#f8fafc",
    color_texto_encabezado: "#FFFFFF",
    mostrar_logo_empresa: true,
    texto_footer: "Gracias por su tiempo",
    mostrar_progreso: true,
    estilo_logo: "sin_fondo", // cuadrado, circular, sin_fondo
    tamaño_logo: "300", // porcentaje: 100, 150, 200, 250, 300, 350, 400
    tamaño_imagen_bienvenida: "100", // porcentaje
    campo_nombre_mostrar: "nombre_cliente", // nombre_cliente, contacto, empresa
    // Opciones avanzadas de espaciado
    espacio_logo_titulo: "8", // px entre logo y título
    espacio_titulo_subtitulo: "4", // px entre título y subtítulo
    espacio_header_contenido: "24", // px entre header y contenido
    padding_header: "24", // px de padding del header
    // Configuración de página de despedida/agradecimiento
    despedida_titulo: "¡Gracias!",
    despedida_mensaje: "Sus comentarios son muy valiosos para nosotros.",
    despedida_mensaje_secundario: "Su respuesta ha sido registrada exitosamente",
    despedida_color_fondo_inicio: "#ecfdf5", // from-green-50
    despedida_color_fondo_fin: "#d1fae5", // to-emerald-100
    despedida_color_titulo: "#166534", // text-green-800
    despedida_color_icono: "#16a34a", // text-green-600
    despedida_color_icono_fondo: "#dcfce7", // bg-green-100
    despedida_color_mensaje_fondo: "#f0fdf4", // bg-green-50
    despedida_color_mensaje_texto: "#15803d", // text-green-700
    despedida_mostrar_logo: false,
    despedida_mostrar_icono_check: true,
    despedida_mostrar_icono_pulgar: true,
    despedida_tamaño_titulo: "2xl", // sm, base, lg, xl, 2xl, 3xl
    despedida_tamaño_mensaje: "base", // sm, base, lg
    despedida_fuente_titulo: "default", // default, serif, mono
    despedida_url_redireccion: "",
    despedida_texto_boton_redireccion: "Visitar sitio web",
    despedida_mostrar_boton_redireccion: false,
    despedida_logo_posicion: "arriba", // arriba, abajo
    despedida_logo_tamaño: "md" // sm, md, lg, xl
  });
  const [emailTemplate, setEmailTemplate] = useState({
    asunto: "Te invitamos a compartir tu opinión",
    encabezado: "Queremos conocer tu opinión",
    mensaje_principal: "Tu feedback es muy importante para nosotros. Por favor, tómate unos minutos para responder esta breve encuesta.",
    texto_boton: "Responder Encuesta",
    mensaje_footer: "Si tienes alguna pregunta, no dudes en contactarnos.",
    color_fondo: "#f8fafc",
    color_encabezado: "#0C314C",
    color_texto_encabezado: "#FFFFFF",
    color_boton: "#45E69A",
    mostrar_logo: true,
    // Opciones avanzadas del logo
    tamano_logo: "150", // px del logo
    estilo_logo: "sin_fondo", // cuadrado, circular, sin_fondo
    // Opciones de espaciado
    espacio_logo_encabezado: "16", // px entre logo y encabezado
    espacio_encabezado_mensaje: "12", // px entre encabezado y mensaje
    espacio_mensaje_boton: "24", // px entre mensaje y botón
    padding_contenido: "32", // px de padding del contenido
    // Opciones adicionales
    ancho_contenido: "600", // px ancho del contenido del email
    redondeo_boton: "8", // px border-radius del botón
    tamano_texto_encabezado: "24", // px tamaño del texto del encabezado
    tamano_texto_mensaje: "16" // px tamaño del texto del mensaje
  });
  const [reminderTemplate, setReminderTemplate] = useState({
    asunto: "Recordatorio: Tu opinión es importante",
    encabezado: "No olvides compartir tu opinión",
    mensaje_principal: "Hace unos días te enviamos una encuesta y aún no hemos recibido tu respuesta. Tu feedback es muy importante para nosotros.",
    texto_boton: "Responder Ahora",
    mensaje_footer: "Gracias por tu tiempo.",
    color_fondo: "#f8fafc",
    color_encabezado: "#D97706",
    color_texto_encabezado: "#FFFFFF",
    color_boton: "#F59E0B",
    mostrar_logo: true,
    mostrar_urgencia: true
  });
  const [emailPreviewHtml, setEmailPreviewHtml] = useState("");
  const [reminderPreviewHtml, setReminderPreviewHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingReminder, setSavingReminder] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingImagen, setUploadingImagen] = useState(false);
  const [uploadingFondo, setUploadingFondo] = useState(false);

  // Estados para campos
  const [campos, setCampos] = useState([]);
  const [loadingCampos, setLoadingCampos] = useState(false);
  const [showCampoDialog, setShowCampoDialog] = useState(false);
  const [editingCampo, setEditingCampo] = useState(null);
  const [campoForm, setCampoForm] = useState({ id: "", nombre: "", requerido_default: false, activo: true });

  // Estados para recordatorios globales
  const [recordatoriosConfig, setRecordatoriosConfig] = useState({
    activo: true,
    dias_primer_recordatorio: 3,
    dias_segundo_recordatorio: 7,
    dias_tercer_recordatorio: 10,
    max_recordatorios: 2,
    hora_envio: "09:00"
  });
  const [savingRecordatorios, setSavingRecordatorios] = useState(false);

  // Estados para alertas de sentimiento
  const [alertasConfig, setAlertasConfig] = useState({
    activo: false,
    destinatarios: [],
    solo_requiere_atencion: true,
    incluir_detractores: true,
    umbral_confianza: 0.6
  });
  const [usuariosDisponibles, setUsuariosDisponibles] = useState([]);
  const [historialAlertas, setHistorialAlertas] = useState([]);
  const [savingAlertas, setSavingAlertas] = useState(false);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [testingAlerta, setTestingAlerta] = useState(false);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
  });

  useEffect(() => {
    fetchConfig();
    fetchEmailTemplate();
    fetchReminderTemplate();
    fetchCampos();
    fetchRecordatoriosConfig();
    fetchAlertasConfig();
    fetchUsuariosDisponibles();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/api/encuestas/portal/configuracion`, getAuthHeaders());
      setConfig({
        logo_url: res.data.logo_url || "",
        imagen_bienvenida_url: res.data.imagen_bienvenida_url || "",
        usar_imagen_bienvenida: res.data.usar_imagen_bienvenida || false,
        color_primario: res.data.color_primario || "#0C314C",
        color_secundario: res.data.color_secundario || "#45E69A",
        color_fondo: res.data.color_fondo || "#f8fafc",
        color_texto_encabezado: res.data.color_texto_encabezado || "#FFFFFF",
        mostrar_logo_empresa: res.data.mostrar_logo_empresa !== false,
        texto_footer: res.data.texto_footer || "Gracias por su tiempo",
        mostrar_progreso: res.data.mostrar_progreso !== false
      });
    } catch (err) {
      console.error("Error loading config:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch campos
  const fetchCampos = async () => {
    setLoadingCampos(true);
    try {
      const res = await axios.get(`${API}/api/encuestas/campos`, getAuthHeaders());
      setCampos(res.data);
    } catch (err) {
      console.error("Error loading campos:", err);
    } finally {
      setLoadingCampos(false);
    }
  };

  // Fetch configuración de recordatorios
  const fetchRecordatoriosConfig = async () => {
    try {
      const res = await axios.get(`${API}/api/encuestas/config/recordatorios`, getAuthHeaders());
      setRecordatoriosConfig({
        activo: res.data.activo !== false,
        dias_primer_recordatorio: res.data.dias_primer_recordatorio || 3,
        dias_segundo_recordatorio: res.data.dias_segundo_recordatorio || 7,
        dias_tercer_recordatorio: res.data.dias_tercer_recordatorio || 10,
        max_recordatorios: res.data.max_recordatorios || 2,
        hora_envio: res.data.hora_envio || "09:00"
      });
    } catch (err) {
      console.error("Error loading recordatorios config:", err);
    }
  };

  // Guardar campo
  const handleSaveCampo = async () => {
    if (!campoForm.id || !campoForm.nombre) {
      toast.error("Complete todos los campos");
      return;
    }

    try {
      if (editingCampo) {
        await axios.put(`${API}/api/encuestas/campos/${editingCampo.id}`, campoForm, getAuthHeaders());
        toast.success("Campo actualizado");
      } else {
        await axios.post(`${API}/api/encuestas/campos`, campoForm, getAuthHeaders());
        toast.success("Campo creado");
      }
      setShowCampoDialog(false);
      setCampoForm({ id: "", nombre: "", requerido_default: false, activo: true });
      setEditingCampo(null);
      fetchCampos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar campo");
    }
  };

  // Eliminar campo
  const handleDeleteCampo = async (campo) => {
    if (campo.sistema) {
      toast.error("No se puede eliminar un campo del sistema");
      return;
    }
    if (!confirm(`¿Está seguro de eliminar el campo "${campo.nombre}"?`)) return;

    try {
      await axios.delete(`${API}/api/encuestas/campos/${campo.id}`, getAuthHeaders());
      toast.success("Campo eliminado");
      fetchCampos();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar campo");
    }
  };

  // Editar campo
  const openEditCampo = (campo) => {
    setCampoForm({
      id: campo.id,
      nombre: campo.nombre,
      requerido_default: campo.requerido_default,
      activo: campo.activo,
      orden: campo.orden
    });
    setEditingCampo(campo);
    setShowCampoDialog(true);
  };

  // Nuevo campo
  const openNewCampo = () => {
    setCampoForm({ id: "", nombre: "", requerido_default: false, activo: true });
    setEditingCampo(null);
    setShowCampoDialog(true);
  };

  // Guardar configuración de recordatorios
  const handleSaveRecordatorios = async () => {
    setSavingRecordatorios(true);
    try {
      await axios.put(`${API}/api/encuestas/config/recordatorios`, recordatoriosConfig, getAuthHeaders());
      toast.success("Configuración de recordatorios guardada");
    } catch (err) {
      toast.error("Error al guardar configuración");
    } finally {
      setSavingRecordatorios(false);
    }
  };

  // ====== FUNCIONES DE ALERTAS DE SENTIMIENTO ======
  
  const fetchAlertasConfig = async () => {
    try {
      const res = await axios.get(`${API}/api/encuestas/portal/alertas`, getAuthHeaders());
      setAlertasConfig({
        activo: res.data.activo || false,
        destinatarios: res.data.destinatarios || [],
        solo_requiere_atencion: res.data.solo_requiere_atencion !== false,
        incluir_detractores: res.data.incluir_detractores !== false,
        umbral_confianza: res.data.umbral_confianza || 0.6
      });
    } catch (err) {
      console.error("Error loading alertas config:", err);
    }
  };

  const fetchUsuariosDisponibles = async () => {
    try {
      const res = await axios.get(`${API}/api/encuestas/portal/alertas/usuarios`, getAuthHeaders());
      setUsuariosDisponibles(res.data);
    } catch (err) {
      console.error("Error loading usuarios:", err);
    }
  };

  const fetchHistorialAlertas = async () => {
    setLoadingHistorial(true);
    try {
      const res = await axios.get(`${API}/api/encuestas/portal/alertas/historial`, getAuthHeaders());
      setHistorialAlertas(res.data);
    } catch (err) {
      console.error("Error loading historial:", err);
    } finally {
      setLoadingHistorial(false);
    }
  };

  const handleSaveAlertas = async () => {
    setSavingAlertas(true);
    try {
      await axios.put(`${API}/api/encuestas/portal/alertas`, alertasConfig, getAuthHeaders());
      toast.success("Configuración de alertas guardada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar configuración");
    } finally {
      setSavingAlertas(false);
    }
  };

  const handleTestAlerta = async () => {
    if (!alertasConfig.destinatarios?.length) {
      toast.error("Agregue al menos un destinatario antes de probar");
      return;
    }
    setTestingAlerta(true);
    try {
      const res = await axios.post(`${API}/api/encuestas/portal/alertas/test`, {}, getAuthHeaders());
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar alerta de prueba");
    } finally {
      setTestingAlerta(false);
    }
  };

  const handleAddDestinatario = (email) => {
    if (email && !alertasConfig.destinatarios.includes(email)) {
      setAlertasConfig({
        ...alertasConfig,
        destinatarios: [...alertasConfig.destinatarios, email]
      });
    }
  };

  const handleRemoveDestinatario = (email) => {
    setAlertasConfig({
      ...alertasConfig,
      destinatarios: alertasConfig.destinatarios.filter(e => e !== email)
    });
  };


  const fetchEmailTemplate = async () => {
    try {
      const res = await axios.get(`${API}/api/encuestas/portal/email-template`, getAuthHeaders());
      setEmailTemplate({
        asunto: res.data.asunto || "Te invitamos a compartir tu opinión",
        encabezado: res.data.encabezado || "Queremos conocer tu opinión",
        mensaje_principal: res.data.mensaje_principal || "Tu feedback es muy importante para nosotros.",
        texto_boton: res.data.texto_boton || "Responder Encuesta",
        mensaje_footer: res.data.mensaje_footer || "Si tienes alguna pregunta, no dudes en contactarnos.",
        color_fondo: res.data.color_fondo || "#f8fafc",
        color_encabezado: res.data.color_encabezado || "#0C314C",
        color_texto_encabezado: res.data.color_texto_encabezado || "#FFFFFF",
        color_boton: res.data.color_boton || "#45E69A",
        mostrar_logo: res.data.mostrar_logo !== false
      });
      // Generate preview
      generateEmailPreview(res.data);
    } catch (err) {
      console.error("Error loading email template:", err);
    }
  };

  const fetchReminderTemplate = async () => {
    try {
      const res = await axios.get(`${API}/api/encuestas/portal/email-template-recordatorio`, getAuthHeaders());
      setReminderTemplate({
        asunto: res.data.asunto || "Recordatorio: Tu opinión es importante",
        encabezado: res.data.encabezado || "No olvides compartir tu opinión",
        mensaje_principal: res.data.mensaje_principal || "Hace unos días te enviamos una encuesta y aún no hemos recibido tu respuesta.",
        texto_boton: res.data.texto_boton || "Responder Ahora",
        mensaje_footer: res.data.mensaje_footer || "Gracias por tu tiempo.",
        color_fondo: res.data.color_fondo || "#f8fafc",
        color_encabezado: res.data.color_encabezado || "#D97706",
        color_texto_encabezado: res.data.color_texto_encabezado || "#FFFFFF",
        color_boton: res.data.color_boton || "#F59E0B",
        mostrar_logo: res.data.mostrar_logo !== false,
        mostrar_urgencia: res.data.mostrar_urgencia !== false
      });
      generateReminderPreview(res.data);
    } catch (err) {
      console.error("Error loading reminder template:", err);
    }
  };

  const generateEmailPreview = async (template) => {
    try {
      const res = await axios.post(`${API}/api/encuestas/portal/email-template/preview`, template, getAuthHeaders());
      setEmailPreviewHtml(res.data.html);
    } catch (err) {
      console.error("Error generating preview:", err);
    }
  };

  const generateReminderPreview = async (template) => {
    try {
      const res = await axios.post(`${API}/api/encuestas/portal/email-template-recordatorio/preview`, template, getAuthHeaders());
      setReminderPreviewHtml(res.data.html);
    } catch (err) {
      console.error("Error generating reminder preview:", err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/encuestas/portal/configuracion`, config, getAuthHeaders());
      toast.success("Configuración guardada correctamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEmailTemplate = async () => {
    setSavingEmail(true);
    try {
      await axios.put(`${API}/api/encuestas/portal/email-template`, emailTemplate, getAuthHeaders());
      toast.success("Plantilla de email guardada correctamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar plantilla");
    } finally {
      setSavingEmail(false);
    }
  };

  const handleSaveReminderTemplate = async () => {
    setSavingReminder(true);
    try {
      await axios.put(`${API}/api/encuestas/portal/email-template-recordatorio`, reminderTemplate, getAuthHeaders());
      toast.success("Plantilla de recordatorio guardada correctamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar plantilla");
    } finally {
      setSavingReminder(false);
    }
  };

  const handleEmailTemplateChange = (field, value) => {
    const newTemplate = { ...emailTemplate, [field]: value };
    setEmailTemplate(newTemplate);
    generateEmailPreview(newTemplate);
  };

  const handleReminderTemplateChange = (field, value) => {
    const newTemplate = { ...reminderTemplate, [field]: value };
    setReminderTemplate(newTemplate);
    generateReminderPreview(newTemplate);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingLogo(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await axios.post(
        `${API}/api/encuestas/portal/subir-logo`,
        formData,
        { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" } }
      );
      setConfig({ ...config, logo_url: res.data.logo_url });
      toast.success("Logo subido correctamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir logo");
    } finally {
      setUploadingLogo(false);
      e.target.value = "";
    }
  };

  const handleImagenUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingImagen(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await axios.post(
        `${API}/api/encuestas/portal/subir-imagen-bienvenida`,
        formData,
        { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" } }
      );
      setConfig({ ...config, imagen_bienvenida_url: res.data.imagen_url });
      toast.success("Imagen subida correctamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir imagen");
    } finally {
      setUploadingImagen(false);
      e.target.value = "";
    }
  };

  const handleFondoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingFondo(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await axios.post(
        `${API}/api/encuestas/portal/subir-imagen-fondo`,
        formData,
        { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" } }
      );
      setConfig({ ...config, imagen_fondo_url: res.data.imagen_url });
      toast.success("Imagen de fondo subida correctamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir imagen de fondo");
    } finally {
      setUploadingFondo(false);
      e.target.value = "";
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-slate-50">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center ml-0 md:ml-64">
          <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 overflow-auto ml-0 md:ml-64">
        <div className="p-6 max-w-6xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <Globe className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-slate-800">Portal Público de Encuestas</h1>
                <p className="text-slate-500">Configure la apariencia del portal y las plantillas de email</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="mb-6 flex-wrap">
              <TabsTrigger value="portal" className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Apariencia
              </TabsTrigger>
              <TabsTrigger value="despedida" className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Despedida Encuesta
              </TabsTrigger>
              <TabsTrigger value="campos" className="flex items-center gap-2">
                <List className="w-4 h-4" />
                Campos
              </TabsTrigger>
              <TabsTrigger value="recordatorios-default" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Recordatorios
              </TabsTrigger>
              <TabsTrigger value="alertas" className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Alertas IA
              </TabsTrigger>
              <TabsTrigger value="email" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email Invitación
              </TabsTrigger>
              <TabsTrigger value="recordatorio" className="flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Email Recordatorio
              </TabsTrigger>
            </TabsList>

            {/* TAB: Portal */}
            <TabsContent value="portal">
              <div className="flex justify-end mb-4">
                <Button onClick={handleSave} disabled={saving} data-testid="save-portal-config-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Guardando..." : "Guardar Cambios"}
                </Button>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Panel de Configuración */}
            <div className="space-y-6 max-h-[85vh] overflow-y-auto pr-2">
              {/* Logo */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Image className="w-5 h-5" />
                    Logo del Portal
                  </CardTitle>
                  <CardDescription>
                    Este logo aparecerá en todas las encuestas que no tengan un logo personalizado
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-4">
                    {config.logo_url ? (
                      <div className="w-72 h-48 border rounded-lg overflow-hidden flex items-center justify-center bg-white shadow-sm p-4">
                        <img src={config.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
                      </div>
                    ) : (
                      <div className="w-72 h-48 border-2 border-dashed rounded-lg flex items-center justify-center bg-slate-50">
                        <Image className="w-12 h-12 text-slate-300" />
                      </div>
                    )}
                    <div className="space-y-2">
                      <div className="relative">
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                          onChange={handleLogoUpload}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          disabled={uploadingLogo}
                          data-testid="upload-logo-input"
                        />
                        <Button variant="outline" disabled={uploadingLogo}>
                          <Upload className="w-4 h-4 mr-2" />
                          {uploadingLogo ? "Subiendo..." : "Subir Logo"}
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500">PNG, JPG, SVG - máx 500KB</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div>
                      <Label>Mostrar logo de la empresa</Label>
                      <p className="text-xs text-slate-500">Si no hay logo personalizado, usar el logo del sistema</p>
                    </div>
                    <Switch
                      checked={config.mostrar_logo_empresa}
                      onCheckedChange={(v) => setConfig({ ...config, mostrar_logo_empresa: v })}
                      data-testid="show-company-logo-switch"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Imagen de Bienvenida */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Image className="w-5 h-5" />
                    Imagen de Bienvenida
                  </CardTitle>
                  <CardDescription>
                    Puede mostrar una imagen en lugar del texto de bienvenida
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Usar imagen de bienvenida</Label>
                      <p className="text-xs text-slate-500">Si está activo, se mostrará la imagen en lugar del texto</p>
                    </div>
                    <Switch
                      checked={config.usar_imagen_bienvenida}
                      onCheckedChange={(v) => setConfig({ ...config, usar_imagen_bienvenida: v })}
                      data-testid="use-welcome-image-switch"
                    />
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {config.imagen_bienvenida_url ? (
                      <div className="w-40 h-24 border rounded-lg overflow-hidden flex items-center justify-center bg-white shadow-sm">
                        <img src={config.imagen_bienvenida_url} alt="Bienvenida" className="max-w-full max-h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-40 h-24 border-2 border-dashed rounded-lg flex items-center justify-center bg-slate-50">
                        <Image className="w-8 h-8 text-slate-300" />
                      </div>
                    )}
                    <div className="space-y-2">
                      <div className="relative">
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                          onChange={handleImagenUpload}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          disabled={uploadingImagen}
                          data-testid="upload-welcome-image-input"
                        />
                        <Button variant="outline" disabled={uploadingImagen}>
                          <Upload className="w-4 h-4 mr-2" />
                          {uploadingImagen ? "Subiendo..." : "Subir Imagen"}
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500">PNG, JPG, GIF, WebP - máx 1MB</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Imagen de Fondo */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Image className="w-5 h-5" />
                    Imagen de Fondo
                  </CardTitle>
                  <CardDescription>
                    Puede usar una imagen como fondo de toda la encuesta
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Usar imagen de fondo</Label>
                      <p className="text-xs text-slate-500">La imagen se mostrará detrás del contenido de la encuesta</p>
                    </div>
                    <Switch
                      checked={config.usar_imagen_fondo}
                      onCheckedChange={(v) => setConfig({ ...config, usar_imagen_fondo: v })}
                      data-testid="use-background-image-switch"
                    />
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {config.imagen_fondo_url ? (
                      <div className="w-40 h-24 border rounded-lg overflow-hidden flex items-center justify-center bg-white shadow-sm">
                        <img src={config.imagen_fondo_url} alt="Fondo" className="max-w-full max-h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-40 h-24 border-2 border-dashed rounded-lg flex items-center justify-center bg-slate-50">
                        <Image className="w-8 h-8 text-slate-300" />
                      </div>
                    )}
                    <div className="space-y-2">
                      <div className="relative">
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                          onChange={handleFondoUpload}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          disabled={uploadingFondo}
                          data-testid="upload-background-image-input"
                        />
                        <Button variant="outline" disabled={uploadingFondo}>
                          <Upload className="w-4 h-4 mr-2" />
                          {uploadingFondo ? "Subiendo..." : "Subir Imagen de Fondo"}
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500">PNG, JPG, GIF, WebP - máx 2MB</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Opciones Avanzadas */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Opciones Avanzadas
                  </CardTitle>
                  <CardDescription>
                    Personalice el comportamiento y datos mostrados
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Campo a mostrar como nombre del cliente</Label>
                    <Select value={config.campo_nombre_mostrar} onValueChange={(v) => setConfig({ ...config, campo_nombre_mostrar: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione campo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nombre_cliente">Nombre del Cliente</SelectItem>
                        <SelectItem value="contacto">Nombre del Contacto</SelectItem>
                        <SelectItem value="empresa">Empresa</SelectItem>
                        <SelectItem value="ninguno">No mostrar nombre</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">Este campo se usará para personalizar el saludo</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Estilo del Logo</Label>
                    <Select value={config.estilo_logo} onValueChange={(v) => setConfig({ ...config, estilo_logo: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione estilo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cuadrado">Cuadrado con fondo blanco</SelectItem>
                        <SelectItem value="circular">Circular con fondo blanco</SelectItem>
                        <SelectItem value="sin_fondo">Sin fondo (transparente)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Tamaño del Logo (%)</Label>
                    <Select value={config.tamaño_logo || "100"} onValueChange={(v) => setConfig({ ...config, tamaño_logo: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione tamaño" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="50">50% - Muy pequeño (40px)</SelectItem>
                        <SelectItem value="100">100% - Normal (80px)</SelectItem>
                        <SelectItem value="150">150% - Mediano (120px)</SelectItem>
                        <SelectItem value="200">200% - Grande (160px)</SelectItem>
                        <SelectItem value="250">250% - Muy grande (200px)</SelectItem>
                        <SelectItem value="300">300% - Extra grande (240px)</SelectItem>
                        <SelectItem value="350">350% - Enorme (280px)</SelectItem>
                        <SelectItem value="400">400% - Máximo (320px)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <Label>Mostrar Logo</Label>
                      <p className="text-xs text-slate-500">Desactive para ocultar el logo del portal</p>
                    </div>
                    <Switch
                      checked={config.mostrar_logo_empresa}
                      onCheckedChange={(v) => setConfig({ ...config, mostrar_logo_empresa: v })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Tamaño de Imagen de Bienvenida (%)</Label>
                    <Select value={config.tamaño_imagen_bienvenida || "100"} onValueChange={(v) => setConfig({ ...config, tamaño_imagen_bienvenida: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione tamaño" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="50">50% - Pequeña</SelectItem>
                        <SelectItem value="75">75% - Mediana</SelectItem>
                        <SelectItem value="100">100% - Normal</SelectItem>
                        <SelectItem value="125">125% - Grande</SelectItem>
                        <SelectItem value="150">150% - Muy grande</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <Label>Usar Imagen de Fondo</Label>
                      <p className="text-xs text-slate-500">Desactive para quitar la imagen de fondo</p>
                    </div>
                    <Switch
                      checked={config.usar_imagen_fondo}
                      onCheckedChange={(v) => setConfig({ ...config, usar_imagen_fondo: v })}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Colores */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Palette className="w-5 h-5" />
                    Colores del Portal
                  </CardTitle>
                  <CardDescription>
                    Personalice los colores para que coincidan con su marca
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Color Primario</Label>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-10 h-10 rounded-lg border shadow-sm cursor-pointer"
                          style={{ backgroundColor: config.color_primario }}
                        >
                          <input
                            type="color"
                            value={config.color_primario}
                            onChange={(e) => setConfig({ ...config, color_primario: e.target.value })}
                            className="w-full h-full opacity-0 cursor-pointer"
                            data-testid="primary-color-input"
                          />
                        </div>
                        <Input
                          value={config.color_primario}
                          onChange={(e) => setConfig({ ...config, color_primario: e.target.value })}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Color Secundario</Label>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-10 h-10 rounded-lg border shadow-sm cursor-pointer"
                          style={{ backgroundColor: config.color_secundario }}
                        >
                          <input
                            type="color"
                            value={config.color_secundario}
                            onChange={(e) => setConfig({ ...config, color_secundario: e.target.value })}
                            className="w-full h-full opacity-0 cursor-pointer"
                            data-testid="secondary-color-input"
                          />
                        </div>
                        <Input
                          value={config.color_secundario}
                          onChange={(e) => setConfig({ ...config, color_secundario: e.target.value })}
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Color de Fondo</Label>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-10 h-10 rounded-lg border shadow-sm cursor-pointer"
                        style={{ backgroundColor: config.color_fondo }}
                      >
                        <input
                          type="color"
                          value={config.color_fondo}
                          onChange={(e) => setConfig({ ...config, color_fondo: e.target.value })}
                          className="w-full h-full opacity-0 cursor-pointer"
                          data-testid="background-color-input"
                        />
                      </div>
                      <Input
                        value={config.color_fondo}
                        onChange={(e) => setConfig({ ...config, color_fondo: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Color Texto Encabezado</Label>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-10 h-10 rounded-lg border shadow-sm cursor-pointer"
                        style={{ backgroundColor: config.color_texto_encabezado }}
                      >
                        <input
                          type="color"
                          value={config.color_texto_encabezado}
                          onChange={(e) => setConfig({ ...config, color_texto_encabezado: e.target.value })}
                          className="w-full h-full opacity-0 cursor-pointer"
                          data-testid="header-text-color-input"
                        />
                      </div>
                      <Input
                        value={config.color_texto_encabezado}
                        onChange={(e) => setConfig({ ...config, color_texto_encabezado: e.target.value })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Opciones Adicionales */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Opciones Adicionales
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Mostrar barra de progreso</Label>
                      <p className="text-xs text-slate-500">Indica al cliente cuánto falta para terminar</p>
                    </div>
                    <Switch
                      checked={config.mostrar_progreso}
                      onCheckedChange={(v) => setConfig({ ...config, mostrar_progreso: v })}
                      data-testid="show-progress-switch"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Texto del pie de página</Label>
                    <Input
                      value={config.texto_footer}
                      onChange={(e) => setConfig({ ...config, texto_footer: e.target.value })}
                      placeholder="Gracias por su tiempo"
                      data-testid="footer-text-input"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Opciones Avanzadas - Espaciado */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Opciones Avanzadas (Espaciado)
                  </CardTitle>
                  <CardDescription>
                    Ajuste los espacios entre elementos del encabezado
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Espacio Logo → Título (px)</Label>
                      <Select 
                        value={config.espacio_logo_titulo || "8"} 
                        onValueChange={(v) => setConfig({ ...config, espacio_logo_titulo: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">0px - Sin espacio</SelectItem>
                          <SelectItem value="4">4px - Mínimo</SelectItem>
                          <SelectItem value="8">8px - Normal</SelectItem>
                          <SelectItem value="12">12px - Medio</SelectItem>
                          <SelectItem value="16">16px - Amplio</SelectItem>
                          <SelectItem value="24">24px - Muy amplio</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Espacio Título → Subtítulo (px)</Label>
                      <Select 
                        value={config.espacio_titulo_subtitulo || "4"} 
                        onValueChange={(v) => setConfig({ ...config, espacio_titulo_subtitulo: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">0px - Sin espacio</SelectItem>
                          <SelectItem value="2">2px - Mínimo</SelectItem>
                          <SelectItem value="4">4px - Normal</SelectItem>
                          <SelectItem value="8">8px - Medio</SelectItem>
                          <SelectItem value="12">12px - Amplio</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Padding del Encabezado (px)</Label>
                      <Select 
                        value={config.padding_header || "24"} 
                        onValueChange={(v) => setConfig({ ...config, padding_header: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="12">12px - Compacto</SelectItem>
                          <SelectItem value="16">16px - Reducido</SelectItem>
                          <SelectItem value="20">20px - Medio</SelectItem>
                          <SelectItem value="24">24px - Normal</SelectItem>
                          <SelectItem value="32">32px - Amplio</SelectItem>
                          <SelectItem value="40">40px - Muy amplio</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Espacio Header → Contenido (px)</Label>
                      <Select 
                        value={config.espacio_header_contenido || "24"} 
                        onValueChange={(v) => setConfig({ ...config, espacio_header_contenido: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="8">8px - Mínimo</SelectItem>
                          <SelectItem value="12">12px - Reducido</SelectItem>
                          <SelectItem value="16">16px - Medio</SelectItem>
                          <SelectItem value="24">24px - Normal</SelectItem>
                          <SelectItem value="32">32px - Amplio</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="pt-2 border-t">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setConfig({
                        ...config,
                        espacio_logo_titulo: "8",
                        espacio_titulo_subtitulo: "4",
                        padding_header: "24",
                        espacio_header_contenido: "24"
                      })}
                    >
                      Restaurar valores por defecto
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Vista Previa */}
            <div className="lg:sticky lg:top-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    Vista Previa del Portal
                  </CardTitle>
                  <CardDescription>
                    Así verán sus clientes el portal de encuestas
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div 
                    className="rounded-xl overflow-hidden shadow-lg border relative"
                    style={{ 
                      backgroundColor: config.color_fondo,
                      backgroundImage: config.usar_imagen_fondo && config.imagen_fondo_url ? `url(${config.imagen_fondo_url})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  >
                    {/* Overlay si hay imagen de fondo */}
                    {config.usar_imagen_fondo && config.imagen_fondo_url && (
                      <div className="absolute inset-0 bg-black/20" />
                    )}
                    
                    <div className="relative z-10 p-4">
                      {/* Header Preview - igual que EncuestaPublicaPage */}
                      <div 
                        className="text-center rounded-xl"
                        style={{ 
                          background: `linear-gradient(135deg, ${config.color_primario} 0%, ${config.color_primario}dd 100%)`,
                          padding: `${config.padding_header || 24}px`,
                          marginBottom: `${config.espacio_header_contenido || 24}px`
                        }}
                      >
                        {config.mostrar_logo_empresa && config.logo_url && (
                          <div 
                            className={`mx-auto flex items-center justify-center p-2 ${
                              config.estilo_logo === 'circular' ? 'rounded-full bg-white shadow-lg' : 
                              config.estilo_logo === 'sin_fondo' ? 'bg-transparent' : 'rounded-xl bg-white shadow-lg'
                            }`}
                            style={{ 
                              width: `${Math.round(80 * (parseInt(config.tamaño_logo || 100) / 100))}px`,
                              height: `${Math.round(80 * (parseInt(config.tamaño_logo || 100) / 100))}px`,
                              marginBottom: `${config.espacio_logo_titulo || 8}px`
                            }}
                          >
                            <img 
                              src={config.logo_url} 
                              alt="Logo"
                              className="max-w-full max-h-full object-contain"
                            />
                          </div>
                        )}
                        <h3 
                          className="text-lg font-bold" 
                          style={{ 
                            color: config.color_texto_encabezado || '#FFFFFF',
                            marginBottom: `${config.espacio_titulo_subtitulo || 4}px`
                          }}
                        >
                          Encuesta de Satisfacción
                        </h3>
                        {config.campo_nombre_mostrar !== 'ninguno' && (
                          <p 
                            className="text-sm opacity-90" 
                            style={{ color: config.color_texto_encabezado || '#FFFFFF' }}
                          >
                            Hola, <strong style={{ color: config.color_texto_encabezado || '#FFFFFF' }}>Cliente Ejemplo</strong>
                          </p>
                        )}
                      </div>

                      {/* Imagen o mensaje de bienvenida */}
                      {config.usar_imagen_bienvenida && config.imagen_bienvenida_url ? (
                        <div 
                          className="mb-4 rounded-xl overflow-hidden shadow-md mx-auto"
                          style={{ 
                            width: `${config.tamaño_imagen_bienvenida || 100}%`,
                            maxWidth: '100%'
                          }}
                        >
                          <img 
                            src={config.imagen_bienvenida_url} 
                            alt="Bienvenida"
                            className="w-full h-auto object-cover"
                          />
                        </div>
                      ) : (
                        <div 
                          className="mb-4 p-4 rounded-lg text-center text-sm bg-white/90"
                          style={{ 
                            borderColor: `${config.color_primario}30`,
                            borderWidth: 1
                          }}
                        >
                          Nos gustaría conocer su opinión sobre nuestro servicio.
                        </div>
                      )}

                      {/* NPS Preview */}
                      <div className="mb-4">
                        <p className="text-sm font-medium mb-2 text-slate-700">
                          ¿Qué tan probable es que nos recomiende?
                        </p>
                        <div className="flex gap-1 justify-center">
                          {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
                            <div
                              key={n}
                              className="w-6 h-6 rounded text-xs flex items-center justify-center text-white font-medium"
                              style={{
                                backgroundColor: n <= 6 ? '#ef4444' : n <= 8 ? '#f59e0b' : config.color_secundario
                              }}
                            >
                              {n}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Progress Preview */}
                      {config.mostrar_progreso && (
                        <div className="mb-4">
                          <div className="flex justify-between text-xs text-slate-500 mb-1">
                            <span>Pregunta 1 de 3</span>
                            <span>33%</span>
                          </div>
                          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full"
                              style={{ width: '33%', backgroundColor: config.color_secundario }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Button Preview */}
                      <Button 
                        className="w-full"
                        style={{ backgroundColor: config.color_primario }}
                      >
                        Siguiente
                      </Button>

                      {/* Footer Preview */}
                      {config.texto_footer && (
                        <p className="text-center text-xs text-slate-400 mt-4">
                          {config.texto_footer}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Enlace para ver portal público */}
                  <div className="mt-4 p-3 bg-muted rounded-lg">
                    <Label className="text-sm font-medium mb-2 block">Ver Portal Público</Label>
                    <p className="text-xs text-muted-foreground mb-2">
                      Abra este enlace para ver cómo se ve el portal desde el lado del cliente
                    </p>
                    <div className="flex gap-2">
                      <Input 
                        value={`${window.location.origin}/encuesta/demo`}
                        readOnly
                        className="text-xs"
                      />
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          window.open(`${window.location.origin}/encuesta/demo`, '_blank');
                        }}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Abrir
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Info Card */}
              <Card className="mt-4 border-blue-200 bg-blue-50">
                <CardContent className="pt-4">
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">Configuración Global</p>
                      <p className="text-blue-600">
                        Esta configuración se aplicará a todas las encuestas por defecto. 
                        Cada plantilla puede tener su propia personalización que sobrescribirá estos valores.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
            </TabsContent>

            {/* TAB: Despedida Encuesta */}
            <TabsContent value="despedida">
              <div className="flex justify-end mb-4">
                <Button onClick={handleSave} disabled={saving} data-testid="save-despedida-config-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Guardando..." : "Guardar Cambios"}
                </Button>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Panel de Configuración */}
                <div className="space-y-4 max-h-[85vh] overflow-y-auto pr-2">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Type className="w-5 h-5" />
                        Textos de Agradecimiento
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label>Título Principal</Label>
                        <Input
                          value={config.despedida_titulo}
                          onChange={(e) => setConfig({...config, despedida_titulo: e.target.value})}
                          placeholder="¡Gracias!"
                          data-testid="despedida-titulo-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Mensaje Principal</Label>
                        <Textarea
                          value={config.despedida_mensaje}
                          onChange={(e) => setConfig({...config, despedida_mensaje: e.target.value})}
                          placeholder="Sus comentarios son muy valiosos para nosotros."
                          rows={2}
                          data-testid="despedida-mensaje-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Mensaje Secundario (confirmación)</Label>
                        <Textarea
                          value={config.despedida_mensaje_secundario}
                          onChange={(e) => setConfig({...config, despedida_mensaje_secundario: e.target.value})}
                          placeholder="Su respuesta ha sido registrada exitosamente"
                          rows={2}
                          data-testid="despedida-mensaje-secundario-input"
                        />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Palette className="w-5 h-5" />
                        Colores
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Color Fondo (inicio gradiente)</Label>
                          <div className="flex gap-2">
                            <Input
                              type="color"
                              value={config.despedida_color_fondo_inicio}
                              onChange={(e) => setConfig({...config, despedida_color_fondo_inicio: e.target.value})}
                              className="w-12 h-10 p-1 cursor-pointer"
                            />
                            <Input
                              value={config.despedida_color_fondo_inicio}
                              onChange={(e) => setConfig({...config, despedida_color_fondo_inicio: e.target.value})}
                              className="flex-1"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Color Fondo (fin gradiente)</Label>
                          <div className="flex gap-2">
                            <Input
                              type="color"
                              value={config.despedida_color_fondo_fin}
                              onChange={(e) => setConfig({...config, despedida_color_fondo_fin: e.target.value})}
                              className="w-12 h-10 p-1 cursor-pointer"
                            />
                            <Input
                              value={config.despedida_color_fondo_fin}
                              onChange={(e) => setConfig({...config, despedida_color_fondo_fin: e.target.value})}
                              className="flex-1"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Color del Título</Label>
                          <div className="flex gap-2">
                            <Input
                              type="color"
                              value={config.despedida_color_titulo}
                              onChange={(e) => setConfig({...config, despedida_color_titulo: e.target.value})}
                              className="w-12 h-10 p-1 cursor-pointer"
                            />
                            <Input
                              value={config.despedida_color_titulo}
                              onChange={(e) => setConfig({...config, despedida_color_titulo: e.target.value})}
                              className="flex-1"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Color del Ícono</Label>
                          <div className="flex gap-2">
                            <Input
                              type="color"
                              value={config.despedida_color_icono}
                              onChange={(e) => setConfig({...config, despedida_color_icono: e.target.value})}
                              className="w-12 h-10 p-1 cursor-pointer"
                            />
                            <Input
                              value={config.despedida_color_icono}
                              onChange={(e) => setConfig({...config, despedida_color_icono: e.target.value})}
                              className="flex-1"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Fondo del Ícono</Label>
                          <div className="flex gap-2">
                            <Input
                              type="color"
                              value={config.despedida_color_icono_fondo}
                              onChange={(e) => setConfig({...config, despedida_color_icono_fondo: e.target.value})}
                              className="w-12 h-10 p-1 cursor-pointer"
                            />
                            <Input
                              value={config.despedida_color_icono_fondo}
                              onChange={(e) => setConfig({...config, despedida_color_icono_fondo: e.target.value})}
                              className="flex-1"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Fondo Mensaje Secundario</Label>
                          <div className="flex gap-2">
                            <Input
                              type="color"
                              value={config.despedida_color_mensaje_fondo}
                              onChange={(e) => setConfig({...config, despedida_color_mensaje_fondo: e.target.value})}
                              className="w-12 h-10 p-1 cursor-pointer"
                            />
                            <Input
                              value={config.despedida_color_mensaje_fondo}
                              onChange={(e) => setConfig({...config, despedida_color_mensaje_fondo: e.target.value})}
                              className="flex-1"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Color Texto Mensaje Secundario</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={config.despedida_color_mensaje_texto}
                            onChange={(e) => setConfig({...config, despedida_color_mensaje_texto: e.target.value})}
                            className="w-12 h-10 p-1 cursor-pointer"
                          />
                          <Input
                            value={config.despedida_color_mensaje_texto}
                            onChange={(e) => setConfig({...config, despedida_color_mensaje_texto: e.target.value})}
                            className="flex-1"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Settings className="w-5 h-5" />
                        Tipografía y Elementos
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Tamaño del Título</Label>
                          <Select
                            value={config.despedida_tamaño_titulo}
                            onValueChange={(val) => setConfig({...config, despedida_tamaño_titulo: val})}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="lg">Pequeño</SelectItem>
                              <SelectItem value="xl">Mediano</SelectItem>
                              <SelectItem value="2xl">Grande</SelectItem>
                              <SelectItem value="3xl">Extra Grande</SelectItem>
                              <SelectItem value="4xl">Muy Grande</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Tamaño del Mensaje</Label>
                          <Select
                            value={config.despedida_tamaño_mensaje}
                            onValueChange={(val) => setConfig({...config, despedida_tamaño_mensaje: val})}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sm">Pequeño</SelectItem>
                              <SelectItem value="base">Normal</SelectItem>
                              <SelectItem value="lg">Grande</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Fuente del Título</Label>
                        <Select
                          value={config.despedida_fuente_titulo}
                          onValueChange={(val) => setConfig({...config, despedida_fuente_titulo: val})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="default">Sans Serif (Default)</SelectItem>
                            <SelectItem value="serif">Serif (Clásica)</SelectItem>
                            <SelectItem value="mono">Monospace</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-3 pt-2">
                        <div className="flex items-center justify-between">
                          <Label className="cursor-pointer">Mostrar ícono de check ✓</Label>
                          <Switch
                            checked={config.despedida_mostrar_icono_check}
                            onCheckedChange={(checked) => setConfig({...config, despedida_mostrar_icono_check: checked})}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <Label className="cursor-pointer">Mostrar ícono de pulgar arriba 👍</Label>
                          <Switch
                            checked={config.despedida_mostrar_icono_pulgar}
                            onCheckedChange={(checked) => setConfig({...config, despedida_mostrar_icono_pulgar: checked})}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Image className="w-5 h-5" />
                        Logo en Despedida
                      </CardTitle>
                      <CardDescription>
                        Muestra el logo de tu empresa en la página de agradecimiento
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label className="cursor-pointer">Mostrar logo</Label>
                        <Switch
                          checked={config.despedida_mostrar_logo}
                          onCheckedChange={(checked) => setConfig({...config, despedida_mostrar_logo: checked})}
                        />
                      </div>
                      
                      {config.despedida_mostrar_logo && (
                        <>
                          <div className="space-y-2">
                            <Label>Posición del Logo</Label>
                            <Select
                              value={config.despedida_logo_posicion || "arriba"}
                              onValueChange={(val) => setConfig({...config, despedida_logo_posicion: val})}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="arriba">Arriba del ícono</SelectItem>
                                <SelectItem value="abajo">Abajo del mensaje</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div className="space-y-2">
                            <Label>Tamaño del Logo</Label>
                            <Select
                              value={config.despedida_logo_tamaño || "md"}
                              onValueChange={(val) => setConfig({...config, despedida_logo_tamaño: val})}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="sm">Pequeño (40px)</SelectItem>
                                <SelectItem value="md">Mediano (60px)</SelectItem>
                                <SelectItem value="lg">Grande (80px)</SelectItem>
                                <SelectItem value="xl">Extra Grande (100px)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {config.logo_url ? (
                            <div className="p-3 bg-slate-50 rounded-lg text-center">
                              <p className="text-xs text-muted-foreground mb-2">Logo actual del portal:</p>
                              <img 
                                src={config.logo_url} 
                                alt="Logo" 
                                className="h-12 mx-auto object-contain"
                              />
                            </div>
                          ) : (
                            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                              <p className="text-xs text-yellow-700">
                                No hay logo configurado. Sube un logo en la pestaña "Apariencia" para mostrarlo aquí.
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <ExternalLink className="w-5 h-5" />
                        Botón de Redirección (Opcional)
                      </CardTitle>
                      <CardDescription>
                        Agregue un botón para redirigir al cliente a su sitio web
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label className="cursor-pointer">Mostrar botón de redirección</Label>
                        <Switch
                          checked={config.despedida_mostrar_boton_redireccion}
                          onCheckedChange={(checked) => setConfig({...config, despedida_mostrar_boton_redireccion: checked})}
                        />
                      </div>
                      
                      {config.despedida_mostrar_boton_redireccion && (
                        <>
                          <div className="space-y-2">
                            <Label>Texto del Botón</Label>
                            <Input
                              value={config.despedida_texto_boton_redireccion}
                              onChange={(e) => setConfig({...config, despedida_texto_boton_redireccion: e.target.value})}
                              placeholder="Visitar sitio web"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>URL de Redirección</Label>
                            <Input
                              value={config.despedida_url_redireccion}
                              onChange={(e) => setConfig({...config, despedida_url_redireccion: e.target.value})}
                              placeholder="https://www.ejemplo.com"
                            />
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Vista Previa */}
                <div className="sticky top-4">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2">
                        <Eye className="w-5 h-5" />
                        Vista Previa
                      </CardTitle>
                      <CardDescription>
                        Así se verá la página de agradecimiento
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div 
                        className="rounded-lg overflow-hidden min-h-[400px] flex items-center justify-center p-4"
                        style={{
                          background: `linear-gradient(to bottom right, ${config.despedida_color_fondo_inicio}, ${config.despedida_color_fondo_fin})`
                        }}
                      >
                        <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 text-center">
                          {/* Logo de empresa (posición arriba) */}
                          {config.despedida_mostrar_logo && config.logo_url && (config.despedida_logo_posicion || "arriba") === "arriba" && (
                            <div className="mb-4">
                              <img 
                                src={config.logo_url} 
                                alt="Logo" 
                                className="mx-auto object-contain"
                                style={{ 
                                  height: config.despedida_logo_tamaño === 'sm' ? '40px' : 
                                          config.despedida_logo_tamaño === 'md' ? '60px' : 
                                          config.despedida_logo_tamaño === 'lg' ? '80px' : 
                                          config.despedida_logo_tamaño === 'xl' ? '100px' : '60px'
                                }}
                              />
                            </div>
                          )}
                          
                          {/* Ícono de Check */}
                          {config.despedida_mostrar_icono_check && (
                            <div 
                              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                              style={{ backgroundColor: config.despedida_color_icono_fondo }}
                            >
                              <CheckCircle 
                                className="w-12 h-12" 
                                style={{ color: config.despedida_color_icono }}
                              />
                            </div>
                          )}
                          
                          {/* Título */}
                          <h2 
                            className={`font-bold mb-2 text-${config.despedida_tamaño_titulo} ${
                              config.despedida_fuente_titulo === 'serif' ? 'font-serif' : 
                              config.despedida_fuente_titulo === 'mono' ? 'font-mono' : ''
                            }`}
                            style={{ color: config.despedida_color_titulo }}
                          >
                            {config.despedida_titulo || '¡Gracias!'}
                          </h2>
                          
                          {/* Mensaje Principal */}
                          <p 
                            className={`text-gray-600 text-${config.despedida_tamaño_mensaje}`}
                          >
                            {config.despedida_mensaje || 'Sus comentarios son muy valiosos para nosotros.'}
                          </p>
                          
                          {/* Mensaje Secundario con ícono */}
                          {config.despedida_mostrar_icono_pulgar && (
                            <div 
                              className="mt-6 p-4 rounded-lg"
                              style={{ backgroundColor: config.despedida_color_mensaje_fondo }}
                            >
                              <ThumbsUp 
                                className="w-8 h-8 mx-auto mb-2" 
                                style={{ color: config.despedida_color_icono }}
                              />
                              <p 
                                className="text-sm"
                                style={{ color: config.despedida_color_mensaje_texto }}
                              >
                                {config.despedida_mensaje_secundario || 'Su respuesta ha sido registrada exitosamente'}
                              </p>
                            </div>
                          )}
                          
                          {/* Logo de empresa (posición abajo) */}
                          {config.despedida_mostrar_logo && config.logo_url && config.despedida_logo_posicion === "abajo" && (
                            <div className="mt-6 pt-4 border-t">
                              <img 
                                src={config.logo_url} 
                                alt="Logo" 
                                className="mx-auto object-contain"
                                style={{ 
                                  height: config.despedida_logo_tamaño === 'sm' ? '40px' : 
                                          config.despedida_logo_tamaño === 'md' ? '60px' : 
                                          config.despedida_logo_tamaño === 'lg' ? '80px' : 
                                          config.despedida_logo_tamaño === 'xl' ? '100px' : '60px'
                                }}
                              />
                            </div>
                          )}
                          
                          {/* Botón de redirección */}
                          {config.despedida_mostrar_boton_redireccion && (
                            <div className="mt-6">
                              <button 
                                className="px-6 py-2 rounded-full font-medium text-white transition-all hover:opacity-90"
                                style={{ backgroundColor: config.despedida_color_icono }}
                              >
                                {config.despedida_texto_boton_redireccion || 'Visitar sitio web'}
                                <ExternalLink className="w-4 h-4 inline ml-2" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Info */}
                  <Card className="mt-4 border-blue-200 bg-blue-50">
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div className="text-sm text-blue-800">
                          <p className="font-medium mb-1">Página de Agradecimiento</p>
                          <p className="text-blue-600">
                            Esta página se mostrará automáticamente cuando el cliente complete la encuesta.
                            Los cambios se aplicarán a todas las encuestas.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            {/* TAB: Campos */}
            <TabsContent value="campos">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-lg font-semibold">Campos de Encuestas</h2>
                  <p className="text-sm text-muted-foreground">Configure los campos disponibles para importar destinatarios</p>
                </div>
                <Button onClick={openNewCampo}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Campo
                </Button>
              </div>

              <Card>
                <CardContent className="p-0">
                  {loadingCampos ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin" />
                    </div>
                  ) : (
                    <div className="divide-y">
                      {campos.map((campo) => (
                        <div key={campo.id} className="flex items-center justify-between p-4 hover:bg-muted/50">
                          <div className="flex items-center gap-4">
                            <GripVertical className="w-4 h-4 text-muted-foreground" />
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{campo.nombre}</span>
                                {campo.sistema && (
                                  <Badge variant="secondary" className="text-xs">Sistema</Badge>
                                )}
                                {campo.requerido_default && (
                                  <Badge variant="outline" className="text-xs">Requerido por defecto</Badge>
                                )}
                                {!campo.activo && (
                                  <Badge variant="destructive" className="text-xs">Inactivo</Badge>
                                )}
                              </div>
                              <span className="text-sm text-muted-foreground">ID: {campo.id}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => openEditCampo(campo)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {!campo.sistema && (
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteCampo(campo)}>
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* TAB: Recordatorios por Defecto */}
            <TabsContent value="recordatorios-default">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-lg font-semibold">Configuración de Recordatorios por Defecto</h2>
                  <p className="text-sm text-muted-foreground">Estos valores se aplicarán a todas las nuevas campañas de encuestas</p>
                </div>
                <Button onClick={handleSaveRecordatorios} disabled={savingRecordatorios}>
                  <Save className="w-4 h-4 mr-2" />
                  {savingRecordatorios ? "Guardando..." : "Guardar Configuración"}
                </Button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      Configuración de Recordatorios
                    </CardTitle>
                    <CardDescription>
                      Define cuándo se enviarán los recordatorios automáticos
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Recordatorios Activos por Defecto</Label>
                        <p className="text-sm text-muted-foreground">Las nuevas campañas tendrán recordatorios activados</p>
                      </div>
                      <Switch
                        checked={recordatoriosConfig.activo}
                        onCheckedChange={(checked) => setRecordatoriosConfig({ ...recordatoriosConfig, activo: checked })}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Primer Recordatorio</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="1"
                            max="30"
                            value={recordatoriosConfig.dias_primer_recordatorio}
                            onChange={(e) => setRecordatoriosConfig({ ...recordatoriosConfig, dias_primer_recordatorio: parseInt(e.target.value) || 3 })}
                          />
                          <span className="text-sm text-muted-foreground whitespace-nowrap">días después</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Segundo Recordatorio</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="1"
                            max="30"
                            value={recordatoriosConfig.dias_segundo_recordatorio}
                            onChange={(e) => setRecordatoriosConfig({ ...recordatoriosConfig, dias_segundo_recordatorio: parseInt(e.target.value) || 7 })}
                          />
                          <span className="text-sm text-muted-foreground whitespace-nowrap">días después</span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Tercer Recordatorio</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="1"
                            max="30"
                            value={recordatoriosConfig.dias_tercer_recordatorio}
                            onChange={(e) => setRecordatoriosConfig({ ...recordatoriosConfig, dias_tercer_recordatorio: parseInt(e.target.value) || 10 })}
                          />
                          <span className="text-sm text-muted-foreground whitespace-nowrap">días después</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Máximo de Recordatorios</Label>
                        <Select 
                          value={String(recordatoriosConfig.max_recordatorios)} 
                          onValueChange={(v) => setRecordatoriosConfig({ ...recordatoriosConfig, max_recordatorios: parseInt(v) })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">1 recordatorio</SelectItem>
                            <SelectItem value="2">2 recordatorios</SelectItem>
                            <SelectItem value="3">3 recordatorios</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Hora de Envío</Label>
                      <Input
                        type="time"
                        value={recordatoriosConfig.hora_envio}
                        onChange={(e) => setRecordatoriosConfig({ ...recordatoriosConfig, hora_envio: e.target.value })}
                      />
                      <p className="text-xs text-muted-foreground">Los recordatorios se enviarán a esta hora</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Vista previa del resumen */}
                <Card>
                  <CardHeader>
                    <CardTitle>Resumen de Recordatorios</CardTitle>
                    <CardDescription>Así se verá la configuración aplicada</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
                      <p className="font-medium text-amber-800">Programación de Envíos:</p>
                      <ul className="space-y-2 text-sm text-amber-700">
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          1er recordatorio: {recordatoriosConfig.dias_primer_recordatorio} días después del envío inicial
                        </li>
                        {recordatoriosConfig.max_recordatorios >= 2 && (
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            2do recordatorio: {recordatoriosConfig.dias_segundo_recordatorio} días después del envío inicial
                          </li>
                        )}
                        {recordatoriosConfig.max_recordatorios >= 3 && (
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            3er recordatorio: {recordatoriosConfig.dias_tercer_recordatorio} días después del envío inicial
                          </li>
                        )}
                        <li className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Hora de envío: {recordatoriosConfig.hora_envio}
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* TAB: Alertas de Sentimiento */}
            <TabsContent value="alertas">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Zap className="w-5 h-5 text-amber-500" />
                    Alertas de Sentimiento con IA
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Configure alertas automáticas cuando se detecten comentarios negativos
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleTestAlerta} disabled={testingAlerta}>
                    <TestTube className="w-4 h-4 mr-2" />
                    {testingAlerta ? "Enviando..." : "Enviar Prueba"}
                  </Button>
                  <Button onClick={handleSaveAlertas} disabled={savingAlertas}>
                    <Save className="w-4 h-4 mr-2" />
                    {savingAlertas ? "Guardando..." : "Guardar Configuración"}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Panel de Configuración */}
                <div className="space-y-6">
                  {/* Activar Alertas */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                        Configuración de Alertas
                      </CardTitle>
                      <CardDescription>
                        Active las alertas para recibir notificaciones cuando la IA detecte comentarios negativos
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <div>
                          <Label className="text-base font-medium">Alertas Activas</Label>
                          <p className="text-sm text-muted-foreground">
                            Enviar alertas cuando se detecten comentarios negativos
                          </p>
                        </div>
                        <Switch
                          checked={alertasConfig.activo}
                          onCheckedChange={(checked) => setAlertasConfig({ ...alertasConfig, activo: checked })}
                          data-testid="alertas-activas-switch"
                        />
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label>Solo cuando requiere atención</Label>
                            <p className="text-xs text-muted-foreground">
                              La IA determina si el comentario necesita acción inmediata
                            </p>
                          </div>
                          <Switch
                            checked={alertasConfig.solo_requiere_atencion}
                            onCheckedChange={(checked) => setAlertasConfig({ ...alertasConfig, solo_requiere_atencion: checked })}
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <Label>Incluir detractores (NPS 0-6)</Label>
                            <p className="text-xs text-muted-foreground">
                              Alertar también cuando el NPS es bajo, aunque el comentario no sea negativo
                            </p>
                          </div>
                          <Switch
                            checked={alertasConfig.incluir_detractores}
                            onCheckedChange={(checked) => setAlertasConfig({ ...alertasConfig, incluir_detractores: checked })}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Umbral de Confianza de IA</Label>
                          <div className="flex items-center gap-4">
                            <input
                              type="range"
                              min="0.3"
                              max="0.95"
                              step="0.05"
                              value={alertasConfig.umbral_confianza}
                              onChange={(e) => setAlertasConfig({ ...alertasConfig, umbral_confianza: parseFloat(e.target.value) })}
                              className="flex-1"
                            />
                            <span className="text-sm font-medium w-12">{(alertasConfig.umbral_confianza * 100).toFixed(0)}%</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Solo enviar alertas si la IA tiene al menos este nivel de confianza
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Destinatarios */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <User className="w-5 h-5" />
                        Destinatarios de Alertas
                      </CardTitle>
                      <CardDescription>
                        Seleccione los usuarios que recibirán las alertas por email
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label>Agregar Usuario</Label>
                        <Select onValueChange={(email) => handleAddDestinatario(email)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar usuario..." />
                          </SelectTrigger>
                          <SelectContent>
                              {usuariosDisponibles
                              .filter(u => !alertasConfig.destinatarios.includes(u.email))
                              .map(user => (
                                <SelectItem key={user.id} value={user.email}>
                                  {user.nombre || user.email} - {user.departamento_nombre || user.role}
                                </SelectItem>
                              ))
                            }
                          </SelectContent>
                        </Select>
                      </div>

                      {alertasConfig.destinatarios.length > 0 ? (
                        <div className="space-y-2">
                          <Label>Usuarios seleccionados ({alertasConfig.destinatarios.length})</Label>
                          <div className="border rounded-lg divide-y">
                            {alertasConfig.destinatarios.map(email => {
                              const user = usuariosDisponibles.find(u => u.email === email);
                              return (
                                <div key={email} className="flex items-center justify-between p-3">
                                  <div>
                                    <p className="font-medium">{user?.nombre || email}</p>
                                    <p className="text-sm text-muted-foreground">
                                      {email} {user?.departamento_nombre && `• ${user.departamento_nombre}`}
                                    </p>
                                  </div>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => handleRemoveDestinatario(email)}
                                  >
                                    <Trash2 className="w-4 h-4 text-destructive" />
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-6 border-2 border-dashed rounded-lg">
                          <User className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                          <p className="text-sm text-muted-foreground">
                            No hay destinatarios configurados
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Seleccione usuarios del menú superior
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Panel de Historial */}
                <div className="space-y-6">
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <History className="w-5 h-5" />
                            Historial de Alertas
                          </CardTitle>
                          <CardDescription>
                            Últimas alertas enviadas por el sistema
                          </CardDescription>
                        </div>
                        <Button variant="outline" size="sm" onClick={fetchHistorialAlertas} disabled={loadingHistorial}>
                          <RefreshCw className={`w-4 h-4 ${loadingHistorial ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {loadingHistorial ? (
                        <div className="flex items-center justify-center py-8">
                          <RefreshCw className="w-6 h-6 animate-spin" />
                        </div>
                      ) : historialAlertas.length > 0 ? (
                        <div className="space-y-3 max-h-[500px] overflow-y-auto">
                          {historialAlertas.map((alerta, idx) => (
                            <div key={idx} className="p-4 border rounded-lg bg-red-50 border-red-200">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <AlertTriangle className="w-4 h-4 text-red-500" />
                                  <span className="font-medium text-sm">{alerta.cliente_nombre}</span>
                                </div>
                                <Badge variant="destructive" className="text-xs">
                                  NPS: {alerta.nps_score}
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-700 mb-2 line-clamp-2">
                                "{alerta.comentario}"
                              </p>
                              <div className="flex items-center justify-between text-xs text-muted-foreground">
                                <span>Campaña: {alerta.campana_nombre}</span>
                                <span>{new Date(alerta.created_at).toLocaleDateString()}</span>
                              </div>
                              {alerta.sentimiento?.palabras_clave?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {alerta.sentimiento.palabras_clave.map((palabra, i) => (
                                    <Badge key={i} variant="outline" className="text-xs bg-red-100">
                                      {palabra}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
                          <p className="font-medium text-green-700">Sin alertas recientes</p>
                          <p className="text-sm text-muted-foreground">
                            No se han detectado comentarios negativos
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Info Card */}
                  <Card className="border-blue-200 bg-blue-50">
                    <CardContent className="pt-4">
                      <div className="flex gap-3">
                        <Zap className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-800">
                          <p className="font-medium mb-1">¿Cómo funciona?</p>
                          <ul className="text-blue-600 space-y-1 text-xs">
                            <li>• La IA analiza cada comentario cuando el cliente responde</li>
                            <li>• Detecta el sentimiento (positivo, negativo, neutro)</li>
                            <li>• Identifica palabras clave y si requiere atención</li>
                            <li>• Envía email automático a los destinatarios configurados</li>
                            <li>• El análisis aparece junto al comentario en los resultados</li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            {/* TAB: Email Template */}
            <TabsContent value="email">
              <div className="flex justify-end mb-4">
                <Button onClick={handleSaveEmailTemplate} disabled={savingEmail} data-testid="save-email-template-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {savingEmail ? "Guardando..." : "Guardar Plantilla"}
                </Button>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Panel de Configuración de Email */}
                <div className="space-y-6">
                  {/* Contenido del Email */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Type className="w-5 h-5" />
                        Contenido del Email
                      </CardTitle>
                      <CardDescription>
                        Personalice el texto que verán sus clientes en el correo de invitación
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label>Asunto del correo</Label>
                        <Input
                          value={emailTemplate.asunto}
                          onChange={(e) => handleEmailTemplateChange('asunto', e.target.value)}
                          placeholder="Te invitamos a compartir tu opinión"
                          data-testid="email-subject-input"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Encabezado</Label>
                        <Input
                          value={emailTemplate.encabezado}
                          onChange={(e) => handleEmailTemplateChange('encabezado', e.target.value)}
                          placeholder="Queremos conocer tu opinión"
                          data-testid="email-header-input"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Mensaje principal</Label>
                        <Textarea
                          value={emailTemplate.mensaje_principal}
                          onChange={(e) => handleEmailTemplateChange('mensaje_principal', e.target.value)}
                          placeholder="Tu feedback es muy importante para nosotros..."
                          rows={3}
                          data-testid="email-message-input"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Texto del botón</Label>
                        <Input
                          value={emailTemplate.texto_boton}
                          onChange={(e) => handleEmailTemplateChange('texto_boton', e.target.value)}
                          placeholder="Responder Encuesta"
                          data-testid="email-button-text-input"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Mensaje del pie de página</Label>
                        <Input
                          value={emailTemplate.mensaje_footer}
                          onChange={(e) => handleEmailTemplateChange('mensaje_footer', e.target.value)}
                          placeholder="Si tienes alguna pregunta..."
                          data-testid="email-footer-input"
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Colores del Email */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Palette className="w-5 h-5" />
                        Colores del Email
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-4 gap-4">
                        <div className="space-y-2">
                          <Label>Encabezado</Label>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-10 h-10 rounded-lg border shadow-sm cursor-pointer"
                              style={{ backgroundColor: emailTemplate.color_encabezado }}
                            >
                              <input
                                type="color"
                                value={emailTemplate.color_encabezado}
                                onChange={(e) => handleEmailTemplateChange('color_encabezado', e.target.value)}
                                className="w-full h-full opacity-0 cursor-pointer"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Texto Encabezado</Label>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-10 h-10 rounded-lg border shadow-sm cursor-pointer"
                              style={{ backgroundColor: emailTemplate.color_texto_encabezado }}
                            >
                              <input
                                type="color"
                                value={emailTemplate.color_texto_encabezado}
                                onChange={(e) => handleEmailTemplateChange('color_texto_encabezado', e.target.value)}
                                className="w-full h-full opacity-0 cursor-pointer"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Botón</Label>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-10 h-10 rounded-lg border shadow-sm cursor-pointer"
                              style={{ backgroundColor: emailTemplate.color_boton }}
                            >
                              <input
                                type="color"
                                value={emailTemplate.color_boton}
                                onChange={(e) => handleEmailTemplateChange('color_boton', e.target.value)}
                                className="w-full h-full opacity-0 cursor-pointer"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Fondo</Label>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-10 h-10 rounded-lg border shadow-sm cursor-pointer"
                              style={{ backgroundColor: emailTemplate.color_fondo }}
                            >
                              <input
                                type="color"
                                value={emailTemplate.color_fondo}
                                onChange={(e) => handleEmailTemplateChange('color_fondo', e.target.value)}
                                className="w-full h-full opacity-0 cursor-pointer"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between pt-4 border-t">
                        <div>
                          <Label>Mostrar logo en el email</Label>
                          <p className="text-xs text-slate-500">Usa el logo configurado en el portal</p>
                        </div>
                        <Switch
                          checked={emailTemplate.mostrar_logo}
                          onCheckedChange={(v) => handleEmailTemplateChange('mostrar_logo', v)}
                          data-testid="email-show-logo-switch"
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Opciones Avanzadas */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Settings className="w-5 h-5" />
                        Opciones Avanzadas
                      </CardTitle>
                      <CardDescription>
                        Personalice el tamaño y estilo del logo en el email
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label>Tamaño del Logo (px)</Label>
                        <Select 
                          value={emailTemplate.tamano_logo || "150"} 
                          onValueChange={(v) => handleEmailTemplateChange('tamano_logo', v)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="60">60px - Muy pequeño</SelectItem>
                            <SelectItem value="80">80px - Pequeño</SelectItem>
                            <SelectItem value="100">100px - Mediano</SelectItem>
                            <SelectItem value="120">120px - Normal</SelectItem>
                            <SelectItem value="150">150px - Grande</SelectItem>
                            <SelectItem value="180">180px - Muy grande</SelectItem>
                            <SelectItem value="200">200px - Extra grande</SelectItem>
                            <SelectItem value="250">250px - Máximo</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500">Ajusta el tamaño del logo en el encabezado del email</p>
                      </div>

                      <div className="space-y-2">
                        <Label>Estilo del Logo</Label>
                        <Select 
                          value={emailTemplate.estilo_logo || "sin_fondo"} 
                          onValueChange={(v) => handleEmailTemplateChange('estilo_logo', v)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sin_fondo">Sin fondo (transparente)</SelectItem>
                            <SelectItem value="cuadrado">Cuadrado con fondo blanco</SelectItem>
                            <SelectItem value="circular">Circular con fondo blanco</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Tamaño del Texto del Encabezado (px)</Label>
                        <Select 
                          value={emailTemplate.tamano_texto_encabezado || "24"} 
                          onValueChange={(v) => handleEmailTemplateChange('tamano_texto_encabezado', v)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="18">18px - Pequeño</SelectItem>
                            <SelectItem value="20">20px - Mediano</SelectItem>
                            <SelectItem value="24">24px - Normal</SelectItem>
                            <SelectItem value="28">28px - Grande</SelectItem>
                            <SelectItem value="32">32px - Muy grande</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Tamaño del Texto del Mensaje (px)</Label>
                        <Select 
                          value={emailTemplate.tamano_texto_mensaje || "16"} 
                          onValueChange={(v) => handleEmailTemplateChange('tamano_texto_mensaje', v)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="14">14px - Pequeño</SelectItem>
                            <SelectItem value="16">16px - Normal</SelectItem>
                            <SelectItem value="18">18px - Grande</SelectItem>
                            <SelectItem value="20">20px - Muy grande</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Opciones de Espaciado */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Settings className="w-5 h-5" />
                        Opciones de Espaciado
                      </CardTitle>
                      <CardDescription>
                        Ajuste los espacios entre elementos del email
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Espacio Logo → Encabezado (px)</Label>
                          <Select 
                            value={emailTemplate.espacio_logo_encabezado || "16"} 
                            onValueChange={(v) => handleEmailTemplateChange('espacio_logo_encabezado', v)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0">0px - Sin espacio</SelectItem>
                              <SelectItem value="8">8px - Mínimo</SelectItem>
                              <SelectItem value="12">12px - Pequeño</SelectItem>
                              <SelectItem value="16">16px - Normal</SelectItem>
                              <SelectItem value="24">24px - Amplio</SelectItem>
                              <SelectItem value="32">32px - Muy amplio</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Espacio Encabezado → Mensaje (px)</Label>
                          <Select 
                            value={emailTemplate.espacio_encabezado_mensaje || "12"} 
                            onValueChange={(v) => handleEmailTemplateChange('espacio_encabezado_mensaje', v)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0">0px - Sin espacio</SelectItem>
                              <SelectItem value="8">8px - Mínimo</SelectItem>
                              <SelectItem value="12">12px - Normal</SelectItem>
                              <SelectItem value="16">16px - Amplio</SelectItem>
                              <SelectItem value="24">24px - Muy amplio</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Espacio Mensaje → Botón (px)</Label>
                          <Select 
                            value={emailTemplate.espacio_mensaje_boton || "24"} 
                            onValueChange={(v) => handleEmailTemplateChange('espacio_mensaje_boton', v)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="12">12px - Compacto</SelectItem>
                              <SelectItem value="16">16px - Reducido</SelectItem>
                              <SelectItem value="24">24px - Normal</SelectItem>
                              <SelectItem value="32">32px - Amplio</SelectItem>
                              <SelectItem value="40">40px - Muy amplio</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Padding del Contenido (px)</Label>
                          <Select 
                            value={emailTemplate.padding_contenido || "32"} 
                            onValueChange={(v) => handleEmailTemplateChange('padding_contenido', v)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="16">16px - Compacto</SelectItem>
                              <SelectItem value="24">24px - Reducido</SelectItem>
                              <SelectItem value="32">32px - Normal</SelectItem>
                              <SelectItem value="40">40px - Amplio</SelectItem>
                              <SelectItem value="48">48px - Muy amplio</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Opciones Adicionales */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Settings className="w-5 h-5" />
                        Opciones Adicionales
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label>Ancho del Contenido (px)</Label>
                        <Select 
                          value={emailTemplate.ancho_contenido || "600"} 
                          onValueChange={(v) => handleEmailTemplateChange('ancho_contenido', v)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="500">500px - Compacto</SelectItem>
                            <SelectItem value="550">550px - Reducido</SelectItem>
                            <SelectItem value="600">600px - Normal</SelectItem>
                            <SelectItem value="650">650px - Amplio</SelectItem>
                            <SelectItem value="700">700px - Muy amplio</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500">Ancho máximo del email (recomendado: 600px para mejor compatibilidad)</p>
                      </div>

                      <div className="space-y-2">
                        <Label>Redondeo del Botón (px)</Label>
                        <Select 
                          value={emailTemplate.redondeo_boton || "8"} 
                          onValueChange={(v) => handleEmailTemplateChange('redondeo_boton', v)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">0px - Cuadrado</SelectItem>
                            <SelectItem value="4">4px - Ligeramente redondeado</SelectItem>
                            <SelectItem value="8">8px - Redondeado</SelectItem>
                            <SelectItem value="16">16px - Muy redondeado</SelectItem>
                            <SelectItem value="24">24px - Pill (ovalado)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Info Card */}
                  <Card className="border-amber-200 bg-amber-50">
                    <CardContent className="pt-4">
                      <div className="flex gap-3">
                        <Send className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-amber-800">
                          <p className="font-medium mb-1">Plantilla de Envío</p>
                          <p className="text-amber-600">
                            Este es el formato del correo que recibirán sus clientes cuando envíe una campaña de encuestas.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Vista Previa del Email */}
                <div className="lg:sticky lg:top-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Eye className="w-5 h-5" />
                        Vista Previa del Email
                      </CardTitle>
                      <CardDescription>
                        Así verán sus clientes el correo de invitación
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="border rounded-lg overflow-hidden shadow-sm bg-white">
                        {emailPreviewHtml ? (
                          <iframe
                            srcDoc={emailPreviewHtml}
                            title="Email Preview"
                            className="w-full h-[500px] border-0"
                            sandbox="allow-same-origin"
                          />
                        ) : (
                          <div className="h-[500px] flex items-center justify-center text-slate-400">
                            <RefreshCw className="w-6 h-6 animate-spin" />
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            {/* TAB: Recordatorio Template */}
            <TabsContent value="recordatorio">
              <div className="flex justify-end mb-4">
                <Button onClick={handleSaveReminderTemplate} disabled={savingReminder} data-testid="save-reminder-template-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {savingReminder ? "Guardando..." : "Guardar Plantilla"}
                </Button>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Panel de Configuración de Recordatorio */}
                <div className="space-y-6">
                  {/* Contenido del Recordatorio */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Bell className="w-5 h-5 text-amber-600" />
                        Contenido del Recordatorio
                      </CardTitle>
                      <CardDescription>
                        Personalice el texto del correo de recordatorio para clientes que no han respondido
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label>Asunto del correo</Label>
                        <Input
                          value={reminderTemplate.asunto}
                          onChange={(e) => handleReminderTemplateChange('asunto', e.target.value)}
                          placeholder="Recordatorio: Tu opinión es importante"
                          data-testid="reminder-subject-input"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Encabezado</Label>
                        <Input
                          value={reminderTemplate.encabezado}
                          onChange={(e) => handleReminderTemplateChange('encabezado', e.target.value)}
                          placeholder="No olvides compartir tu opinión"
                          data-testid="reminder-header-input"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Mensaje principal</Label>
                        <Textarea
                          value={reminderTemplate.mensaje_principal}
                          onChange={(e) => handleReminderTemplateChange('mensaje_principal', e.target.value)}
                          placeholder="Hace unos días te enviamos una encuesta..."
                          rows={3}
                          data-testid="reminder-message-input"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Texto del botón</Label>
                        <Input
                          value={reminderTemplate.texto_boton}
                          onChange={(e) => handleReminderTemplateChange('texto_boton', e.target.value)}
                          placeholder="Responder Ahora"
                          data-testid="reminder-button-text-input"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Mensaje del pie de página</Label>
                        <Input
                          value={reminderTemplate.mensaje_footer}
                          onChange={(e) => handleReminderTemplateChange('mensaje_footer', e.target.value)}
                          placeholder="Gracias por tu tiempo."
                          data-testid="reminder-footer-input"
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Colores del Recordatorio */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Palette className="w-5 h-5" />
                        Colores del Recordatorio
                      </CardTitle>
                      <CardDescription>
                        Los recordatorios usan colores más llamativos para urgencia
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-4 gap-4">
                        <div className="space-y-2">
                          <Label>Encabezado</Label>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-10 h-10 rounded-lg border shadow-sm cursor-pointer"
                              style={{ backgroundColor: reminderTemplate.color_encabezado }}
                            >
                              <input
                                type="color"
                                value={reminderTemplate.color_encabezado}
                                onChange={(e) => handleReminderTemplateChange('color_encabezado', e.target.value)}
                                className="w-full h-full opacity-0 cursor-pointer"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Texto Encabezado</Label>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-10 h-10 rounded-lg border shadow-sm cursor-pointer"
                              style={{ backgroundColor: reminderTemplate.color_texto_encabezado }}
                            >
                              <input
                                type="color"
                                value={reminderTemplate.color_texto_encabezado}
                                onChange={(e) => handleReminderTemplateChange('color_texto_encabezado', e.target.value)}
                                className="w-full h-full opacity-0 cursor-pointer"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Botón</Label>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-10 h-10 rounded-lg border shadow-sm cursor-pointer"
                              style={{ backgroundColor: reminderTemplate.color_boton }}
                            >
                              <input
                                type="color"
                                value={reminderTemplate.color_boton}
                                onChange={(e) => handleReminderTemplateChange('color_boton', e.target.value)}
                                className="w-full h-full opacity-0 cursor-pointer"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Fondo</Label>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-10 h-10 rounded-lg border shadow-sm cursor-pointer"
                              style={{ backgroundColor: reminderTemplate.color_fondo }}
                            >
                              <input
                                type="color"
                                value={reminderTemplate.color_fondo}
                                onChange={(e) => handleReminderTemplateChange('color_fondo', e.target.value)}
                                className="w-full h-full opacity-0 cursor-pointer"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between pt-4 border-t">
                        <div>
                          <Label>Mostrar logo en el email</Label>
                          <p className="text-xs text-slate-500">Usa el logo configurado en el portal</p>
                        </div>
                        <Switch
                          checked={reminderTemplate.mostrar_logo}
                          onCheckedChange={(v) => handleReminderTemplateChange('mostrar_logo', v)}
                          data-testid="reminder-show-logo-switch"
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Mostrar mensaje de urgencia</Label>
                          <p className="text-xs text-slate-500">Muestra una alerta visual de recordatorio</p>
                        </div>
                        <Switch
                          checked={reminderTemplate.mostrar_urgencia}
                          onCheckedChange={(v) => handleReminderTemplateChange('mostrar_urgencia', v)}
                          data-testid="reminder-show-urgency-switch"
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Info Card */}
                  <Card className="border-amber-200 bg-amber-50">
                    <CardContent className="pt-4">
                      <div className="flex gap-3">
                        <Bell className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-amber-800">
                          <p className="font-medium mb-1">Plantilla de Recordatorio</p>
                          <p className="text-amber-600">
                            Este correo se enviará automáticamente a los clientes que no han respondido la encuesta, según la configuración de recordatorios de cada campaña.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Vista Previa del Recordatorio */}
                <div className="lg:sticky lg:top-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Eye className="w-5 h-5" />
                        Vista Previa del Recordatorio
                      </CardTitle>
                      <CardDescription>
                        Así verán sus clientes el correo de recordatorio
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="border rounded-lg overflow-hidden shadow-sm bg-white">
                        {reminderPreviewHtml ? (
                          <iframe
                            srcDoc={reminderPreviewHtml}
                            title="Reminder Preview"
                            className="w-full h-[500px] border-0"
                            sandbox="allow-same-origin"
                          />
                        ) : (
                          <div className="h-[500px] flex items-center justify-center text-slate-400">
                            <RefreshCw className="w-6 h-6 animate-spin" />
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Dialog para crear/editar campo */}
      <Dialog open={showCampoDialog} onOpenChange={setShowCampoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCampo ? "Editar Campo" : "Nuevo Campo"}</DialogTitle>
            <DialogDescription>
              {editingCampo ? "Modifique los datos del campo" : "Configure un nuevo campo para las encuestas"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>ID del Campo *</Label>
              <Input
                value={campoForm.id}
                onChange={(e) => setCampoForm({ ...campoForm, id: e.target.value.toLowerCase().replace(/\s+/g, "_") })}
                placeholder="ejemplo: numero_factura"
                disabled={!!editingCampo}
              />
              <p className="text-xs text-muted-foreground">Identificador único (sin espacios, usar guiones bajos)</p>
            </div>

            <div className="space-y-2">
              <Label>Nombre del Campo *</Label>
              <Input
                value={campoForm.nombre}
                onChange={(e) => setCampoForm({ ...campoForm, nombre: e.target.value })}
                placeholder="Número de Factura"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Requerido por Defecto</Label>
                <p className="text-xs text-muted-foreground">Este campo será obligatorio al importar</p>
              </div>
              <Switch
                checked={campoForm.requerido_default}
                onCheckedChange={(checked) => setCampoForm({ ...campoForm, requerido_default: checked })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Campo Activo</Label>
                <p className="text-xs text-muted-foreground">Mostrar este campo en las opciones</p>
              </div>
              <Switch
                checked={campoForm.activo}
                onCheckedChange={(checked) => setCampoForm({ ...campoForm, activo: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCampoDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveCampo}>
              <Save className="w-4 h-4 mr-2" />
              {editingCampo ? "Actualizar" : "Crear Campo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EncuestasPortalPage;
